import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Download } from 'lucide-react';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Checkbox } from '@/components/ui/Checkbox';
import { FileUpload } from '@/components/ui/FileUpload';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { getErrorMessage } from '@/services/apiClient';
import type { SelectOption } from '@/types/common.types';
import { DEFAULT_QUOTE, parseCsv, toCsv, type ParsedCsv, defuseFormula } from '@/lib/csv';
import {
  assignableModes,
  autoDetectMapping,
  buildFieldList,
  buildRow,
  chunk,
  compileMapping,
  fieldByKey,
  isReadyToImport,
  missingRequiredFields,
  modesFor,
  mappingWarnings,
  remapPreserving,
  type ColumnMapping,
  type ImportCatalogs,
  type PopulationMode,
} from '@/lib/leadImport';
import { getFilterOptions } from '@/services/filters.service';
import type { FilterOption } from '@/types/filters.types';
import {
  BATCH_SIZE,
  MAX_FILE_BYTES,
  MAX_ROWS,
  addBatch,
  emptyTally,
  importLeadBatch,
  isSpreadsheet,
  parseSpreadsheet,
  type LeadImportTally,
} from '@/services/leadImport.service';

/**
 * Add leads from a spreadsheet: choose a file, match its columns to lead fields,
 * import.
 *
 * The file is parsed in the browser and posted as mapped rows in batches, so
 * there is no upload, no background run to poll, and no new table — a practice
 * importing a few hundred leads sees the result immediately.
 *
 * Reached from the Leads page and from Admin -> Import -> Leads. Both land here,
 * and both show only when the account has the opt-in Lead Import module
 * (LEAD_IMPORT_MODULE_KEY); AdminPage guards the route the same way.
 *
 * When Admin -> Import -> Leads is Locked (plan 2026-10-10-001, U7) the page
 * renders with no file picker, so nothing can be read or imported, and says
 * nothing about why (R18). The Leads page route has no Admin level, so it is
 * unaffected.
 */

type Step = 'upload' | 'mapping' | 'importing' | 'summary';

/** How many sample values to show beside a heading on the mapping step. */
const SAMPLE_COUNT = 2;

const DONT_IMPORT = '__skip__';

/** What a spreadsheet export actually uses, in rough order of likelihood. */
const SEPARATOR_OPTIONS: SelectOption<string>[] = [
  { label: 'Comma  ,', value: ',' },
  { label: 'Semicolon  ;', value: ';' },
  { label: 'Tab', value: '\t' },
  { label: 'Pipe  |', value: '|' },
];

const QUALIFIER_OPTIONS: SelectOption<string>[] = [
  { label: 'Double quote  "', value: '"' },
  { label: "Single quote  '", value: "'" },
  { label: 'Nothing', value: '' },
];

export function LeadsImportPage() {
  const navigate = useNavigate();
  const locked = useAdminPageLevel() === 'locked';

  const [step, setStep] = useState<Step>('upload');
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping>([]);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [leadSource, setLeadSource] = useState('');
  const [fileError, setFileError] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  /** Kept so changing the separator can re-parse without re-reading the file. */
  const [rawText, setRawText] = useState('');
  const [catalogs, setCatalogs] = useState<ImportCatalogs>({});
  const [defaults, setDefaults] = useState<Record<string, string>>({});
  const [defaultSubject, setDefaultSubject] = useState('');
  const [delimiter, setDelimiter] = useState(',');
  const [quote, setQuote] = useState(DEFAULT_QUOTE);
  const [importError, setImportError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [tally, setTally] = useState<LeadImportTally>(emptyTally);
  /** Rows completed before an import stopped early, or null if it finished. */
  const [stoppedAfter, setStoppedAfter] = useState<number | null>(null);

  /**
   * Translate a reported row (its position in the file's data rows, offset by
   * the heading) into the line number the user's spreadsheet actually shows.
   * Blank lines are dropped during parsing and a quoted value can span several
   * lines, so the two diverge on real files.
   */
  function sourceLine(reportedRow: number): number {
    return parsed?.rowLines[reportedRow - 2] ?? reportedRow;
  }

  const rowCount = parsed?.rows.length ?? 0;
  const missing = missingRequiredFields(mapping);
  const ready = isReadyToImport(mapping);

  // Closing the tab mid-import stops the remaining leads being added, and the
  // ones already added stay added — so warn before that happens rather than
  // leaving the user to discover a half-finished list.
  useEffect(() => {
    if (step !== 'importing') return undefined;

    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Browsers show their own wording; a non-empty value is what triggers it.
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [step]);

  /**
   * Every list the mapping step needs, in one request. Fetched up front so a
   * heading that matches no facility can be flagged while the user is still
   * mapping, rather than turning up as a warning after the import ran.
   */
  useEffect(() => {
    let cancelled = false;

    getFilterOptions(['facility', 'leadSource', 'planType', 'user', 'leadCustomField'])
      .then((options) => {
        if (cancelled) return;

        const toEntries = (list?: FilterOption[]) =>
          (list ?? [])
            // The lead-source list carries category headers as `filtersgroup-N`
            // pseudo-options; they are not sources and cannot be matched.
            .filter((option) => !option.value.startsWith('filtersgroup-'))
            .map((option) => ({ id: option.value, label: option.label }));

        setCatalogs({
          facility: toEntries(options.facility),
          leadSource: toEntries(options.leadSource),
          planType: toEntries(options.planType),
          user: toEntries(options.user),
          customFields: toEntries(options.leadCustomField),
        });
      })
      // A failed fetch only costs the previews and the custom-field list; the
      // import still works, and the server is what resolves anything anyway.
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * The fields a whole import can be given a value for, in the order they are
   * offered. Each is skipped when the account has nothing to choose from, so a
   * practice with no plan types never sees an empty dropdown.
   */
  const defaultable = useMemo(
    () =>
      (
        [
          ['facilityId', 'facility', 'Facility'],
          ['leadSourceId', 'leadSource', 'Lead source'],
          ['planTypeId', 'planType', 'Plan type'],
          ['assignedUserId', 'user', 'Assigned to'],
        ] as const
      )
        .map(([field, catalog, label]) => ({
          field,
          label,
          entries: catalogs[catalog] ?? [],
        }))
        .filter((entry) => entry.entries.length > 0),
    [catalogs],
  );

  function setDefaultFor(field: string, value: string | null) {
    setDefaults((current) => {
      const next = { ...current };
      // An empty choice is the absence of a default, not a value to send.
      if (value === null || value === '') delete next[field];
      else next[field] = value;
      return next;
    });
  }

  /**
   * Everything applied to the whole batch: the chosen catalog ids plus the
   * subject text. Merged here so the request carries one object, and an empty
   * subject is absent rather than sent as an empty string.
   */
  const batchDefaults = useMemo(
    () =>
      defaultSubject.trim() === ''
        ? defaults
        : { ...defaults, subject: defaultSubject.trim() },
    [defaults, defaultSubject],
  );

  /** Built-in fields plus this account's own custom lead fields. */
  const fields = useMemo(() => buildFieldList(catalogs), [catalogs]);

  // ─── Step 1: choose a file ──────────────────────────────────────────────

  async function handleFile(files: File[]) {
    const file = files[0];
    if (locked || !file) return;

    setFileError(null);

    if (file.size > MAX_FILE_BYTES) {
      setFileError('That file is too large. Split it into smaller files and add them one at a time.');
      return;
    }

    // Decided on the file's contents, not its name: people rename
    // spreadsheets to .csv often enough that trusting the extension would send
    // a real workbook down the CSV path and produce nonsense rows.
    let workbook = false;
    try {
      workbook = await isSpreadsheet(file);
    } catch {
      workbook = false;
    }

    if (!workbook && !/\.(csv|txt)$/i.test(file.name)) {
      setFileError(
        "That file isn't a CSV or Excel file. Save your spreadsheet as CSV and try again.",
      );
      return;
    }

    let result: ParsedCsv;

    if (workbook) {
      // A workbook cannot be read in the browser, so this one step is a server
      // round trip. It comes back in exactly the CSV shape, so nothing after
      // this point knows the difference.
      setParsing(true);
      try {
        result = await parseSpreadsheet(file);
      } catch (error) {
        setFileError(spreadsheetErrorMessage(error));
        return;
      } finally {
        setParsing(false);
      }
    } else {
      let text: string;
      try {
        text = await readFile(file);
      } catch {
        setFileError("We couldn't read that file. Try saving it again as CSV.");
        return;
      }

      setRawText(text);
      result = parseCsv(text);
    }

    if (result.headers.length === 0) {
      setFileError(
        "We couldn't read the column headings. Make sure the first row of the file names each column.",
      );
      return;
    }

    if (result.rows.length === 0) {
      setFileError('That file has headings but no leads in it.');
      return;
    }

    if (result.rows.length > MAX_ROWS) {
      setFileError(
        `That file has ${result.rows.length.toLocaleString()} leads. ` +
          `Please split it into files of ${MAX_ROWS.toLocaleString()} or fewer and add them one at a time.`,
      );
      return;
    }

    setFileName(file.name);
    setDelimiter(result.delimiter);
    setQuote(DEFAULT_QUOTE);
    setParsed(result);
    setMapping(
      autoDetectMapping(result.headers, {
        catalogs,
        fields: buildFieldList(catalogs),
        // A few rows are enough to tell a tick-box column from a text one.
        samples: result.headers.map((_, i) => result.rows.slice(0, 20).map((r) => r[i] ?? '')),
      }),
    );
    setLeadSource(defaultLeadSource(file.name));
    setStep('mapping');
  }

  /**
   * Re-read the file with a different separator or qualifier.
   *
   * Choices are carried across by heading text rather than position, because a
   * different separator can change the column count entirely — re-running
   * auto-detection over the user's hand-corrections would be worse than losing
   * the columns that genuinely disappeared.
   */
  function reparse(nextDelimiter: string, nextQuote: string) {
    setDelimiter(nextDelimiter);
    setQuote(nextQuote);
    if (rawText === '' || !parsed) return;

    const next = parseCsv(rawText, { delimiter: nextDelimiter, quote: nextQuote });
    if (next.headers.length === 0) return;

    setMapping((current) => remapPreserving(current, parsed.headers, next.headers));
    setParsed(next);
  }

  // ─── Step 2: match the columns ──────────────────────────────────────────

  /**
   * The options for one column's field dropdown.
   *
   * Availability is per column now rather than a shared "already used" set: a
   * field taken by another column is unavailable, except in tick-box mode where
   * many columns feed one field on purpose.
   */
  const fieldOptionsFor = useCallback(
    (index: number): SelectOption<string>[] => [
      { label: "Don't import this column", value: DONT_IMPORT },
      ...fields.map((field) => ({
        label: field.label,
        value: field.key,
        description: field.hint,
        disabled: assignableModes(mapping, index, field.key, fields).length === 0,
      })),
    ],
    [mapping, fields],
  );

  /**
   * How a column produces its value, in the user's words.
   *
   * The tick-box label names the actual heading, which is what makes
   * many-columns-to-one-field legible at the point of choosing it.
   */
  function modeOptionsFor(index: number): SelectOption<string>[] {
    const field = fieldByKey(mapping[index]?.field ?? null, fields);
    const heading = parsed?.headers[index] ?? '';
    const noun = (field?.label ?? '').toLowerCase();

    return modesFor(field).map((mode) => ({
      value: mode,
      disabled: field ? !assignableModes(mapping, index, field.key, fields).includes(mode) : false,
      label:
        mode === 'value' ? 'Use the text in the cell'
          : mode === 'name' ? `Match the name to a ${noun}`
            : mode === 'id' ? 'Match the ID number'
              : `This column is a tick box for "${heading}"`,
    }));
  }

  function setMode(index: number, mode: string | null) {
    setMapping((current) => {
      const next = [...current];
      next[index] = { ...next[index], mode: (mode ?? 'value') as PopulationMode };
      return next;
    });
  }

  /** Fields fed by tick-box columns, for the explanation above the table. */
  const flagGroups = useMemo(() => {
    if (!parsed) return [];

    return compileMapping(mapping, parsed.headers).flags.map(({ field, columns }) => ({
      label: fieldByKey(field, fields)?.label ?? field,
      headings: columns.map((c) => c.heading),
    }));
  }, [mapping, parsed, fields]);

  /** Headings that match nothing, surfaced before the import rather than after. */
  const columnWarnings = useMemo(() => {
    if (!parsed) return new Map<number, string>();

    return new Map(
      mappingWarnings(mapping, parsed.headers, parsed.rows, catalogs, fields)
        .map((warning) => [warning.columnIndex, warning.message] as const),
    );
  }, [mapping, parsed, catalogs, fields]);

  function setColumn(index: number, value: string | null) {
    setMapping((current) => {
      const next = [...current];
      const field = value === null || value === DONT_IMPORT ? null : value;
      // Default to the first mode still free for this field, so choosing
      // "Facility" starts as a name match rather than an impossible
      // plain-text write — and choosing a field that tick-box columns
      // already feed joins them instead of colliding with them.
      const mode =
        field === null
          ? 'value'
          : assignableModes(current, index, field, fields)[0] ??
            modesFor(fieldByKey(field, fields))[0];
      next[index] = { field, mode };
      return next;
    });
  }

  function samplesFor(index: number): string[] {
    if (!parsed) return [];

    return parsed.rows
      .map((row) => (row[index] ?? '').trim())
      .filter((value) => value !== '')
      .slice(0, SAMPLE_COUNT);
  }

  // ─── Step 3: import ─────────────────────────────────────────────────────

  async function runImport() {
    if (locked || !parsed) return;

    setImportError(null);
    setProgress(0);
    setStep('importing');

    // Compiled once: tick-box grouping and its file-order tie-break are
    // properties of the file, not of any single row.
    const compiled = compileMapping(mapping, parsed.headers);
    const rows = parsed.rows.map((cells) => buildRow(compiled, cells));
    const batches = chunk(rows, BATCH_SIZE);

    let running = emptyTally();
    let done = 0;

    try {
      for (const [index, batch] of batches.entries()) {
        const result = await importLeadBatch({
          rows: batch,
          skipDuplicates,
          leadSource: leadSource.trim(),
          // Row 1 is the heading row, so the first lead is row 2 in the
          // spreadsheet the user is looking at.
          rowOffset: index * BATCH_SIZE + 2,
          defaults: batchDefaults,
        });

        running = addBatch(running, result);
        done += batch.length;

        setTally(running);
        setProgress(done);
      }

      setStoppedAfter(null);
      setStep('summary');
    } catch (error) {
      setImportError(importErrorMessage(error));
      // Whatever landed before the failure is real, so show it rather than
      // implying nothing happened — and record where it stopped, so the rows it
      // never reached are named instead of quietly missing from both the counts
      // and the download.
      setStoppedAfter(done);
      setTally(running);
      setStep('summary');
    }
  }

  function downloadUnimported() {
    if (!parsed) return;

    // Built from the uncapped `notAdded` list, never from `results` — that one
    // is truncated for rendering, and a recovery file missing most of its rows
    // is worse than no file.
    const byRow = new Map(tally.notAdded.map((r) => [r.row, r.reason]));
    const rows: string[][] = [[...parsed.headers, 'Reason']];

    parsed.rows.forEach((cells, index) => {
      const reason = byRow.get(index + 2);
      // Rows the import never reached belong in this file too — otherwise they
      // are simply missing, with nothing telling the user they were skipped.
      const unattempted = stoppedAfter !== null && index >= stoppedAfter;

      if (reason !== undefined) {
        rows.push([...cells.map(defuseFormula), reason]);
      } else if (unattempted) {
        rows.push([...cells.map(defuseFormula), 'The import stopped before reaching this row.']);
      }
    });

    download(`${stripExtension(fileName)} - not added.csv`, toCsv(rows));
  }

  /** Rows that were never attempted because the import stopped early. */
  const unattempted = stoppedAfter === null ? 0 : Math.max(0, rowCount - stoppedAfter);
  const hasDownload = tally.notAdded.length > 0 || unattempted > 0;

  function startOver() {
    setStep('upload');
    setParsed(null);
    setMapping([]);
    setFileName('');
    setRawText('');
    setTally(emptyTally());
    setProgress(0);
    setImportError(null);
    setFileError(null);
  }

  // ─── Render ─────────────────────────────────────────────────────────────

  // p-6 encloses PageHeader, matching ImportAdmin. The shell adds no padding
  // (Layout's <main> is bare) and PageHeader carries only mb-6, so without it
  // the title sits at the content origin and is painted over by the sidebar's
  // collapse chevron (absolute, z-30, overlapping x=0-12px).
  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Add leads from a spreadsheet"
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate('/leads')}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to leads
          </Button>
        }
      />

      {step === 'upload' && (
        <Card className="space-y-4">
          {locked ? (
            <p className="text-sm text-slate-600">
              Leads are added from a CSV or Excel file whose columns are matched to the
              details Prospera keeps for a lead.
            </p>
          ) : (
            <>
              <p className="text-sm text-slate-600">
                Choose a CSV or Excel file of leads. On the next step you'll match its
                columns to the details Prospera keeps for a lead.
              </p>

              <FileUpload
                accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                multiple={false}
                onChange={handleFile}
                disabled={parsing}
              />
            </>
          )}

          {parsing && (
            <p className="text-sm text-slate-600">Reading your spreadsheet…</p>
          )}

          {fileError && (
            <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {fileError}
            </p>
          )}

          <div className="border-t border-border pt-4">
            <Button variant="ghost" size="sm" onClick={downloadSample}>
              <Download className="mr-1 h-4 w-4" />
              Download an example spreadsheet
            </Button>
            <p className="mt-1 text-xs text-slate-500">
              Start from this if you're building the file yourself.
            </p>
          </div>
        </Card>
      )}

      {step === 'mapping' && parsed && !locked && (
        <>
          <Card className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-medium text-slate-900">{fileName}</p>
                <p className="text-sm text-slate-600">
                  {rowCount.toLocaleString()} {rowCount === 1 ? 'lead' : 'leads'} found
                  {parsed.sheetName && ` · sheet "${parsed.sheetName}"`}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={startOver}>
                Choose a different file
              </Button>
            </div>

            {/* A workbook has no separator, so these only apply to a CSV. */}
            {!parsed.sheetName && (
              <div className="flex flex-wrap gap-4 border-t border-border pt-4">
                <div className="w-56">
                  <FormField
                    label="Separator"
                    helpText="Change this if the columns below look wrong."
                  >
                    <Select<string>
                      options={SEPARATOR_OPTIONS}
                      value={delimiter}
                      onChange={(value) => reparse(value ?? ',', quote)}
                    />
                  </FormField>
                </div>
                <div className="w-56">
                  <FormField label="Text wrapped in">
                    <Select<string>
                      options={QUALIFIER_OPTIONS}
                      value={quote}
                      onChange={(value) => reparse(delimiter, value ?? DEFAULT_QUOTE)}
                    />
                  </FormField>
                </div>
              </div>
            )}

            {missing.length > 0 ? (
              <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                Choose which columns hold the {missing.join(' and ').toLowerCase()} before importing.
              </p>
            ) : (
              <p className="flex items-center gap-1 text-sm text-emerald-700">
                <Check className="h-4 w-4" />
                Ready to import.
              </p>
            )}
          </Card>

          {flagGroups.length > 0 && (
            <Card className="space-y-2">
              {flagGroups.map((group) => (
                <p key={group.label} className="text-sm text-slate-700">
                  <span className="font-medium">{group.label}</span> is set from{' '}
                  {group.headings.length} tick-box{' '}
                  {group.headings.length === 1 ? 'column' : 'columns'}:{' '}
                  <span className="font-medium">{group.headings.join(', ')}</span>. The first
                  ticked column in each row wins.
                </p>
              ))}
            </Card>
          )}

          <Card noPadding>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-slate-50 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Column in your file</th>
                    <th className="px-4 py-3">Example</th>
                    <th className="px-4 py-3 w-64">Add it as</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.headers.map((header, index) => {
                    const samples = samplesFor(index);

                    return (
                      <tr key={`${header}-${index}`} className="border-b border-border last:border-0">
                        <td className="px-4 py-3 font-medium text-slate-900">
                          {header || <span className="text-slate-400">(no heading)</span>}
                        </td>
                        <td className="px-4 py-3 text-slate-500">
                          {samples.length > 0 ? (
                            <span className="block max-w-xs truncate" title={samples.join(' · ')}>
                              {samples.join(' · ')}
                            </span>
                          ) : (
                            <span className="text-slate-400">Empty</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="space-y-2">
                            <Select<string>
                              options={fieldOptionsFor(index)}
                              value={mapping[index]?.field ?? DONT_IMPORT}
                              onChange={(value) => setColumn(index, value)}
                            />
                            {modeOptionsFor(index).length > 1 && (
                              <Select<string>
                                options={modeOptionsFor(index)}
                                value={mapping[index]?.mode ?? 'value'}
                                onChange={(value) => setMode(index, value)}
                              />
                            )}
                            {columnWarnings.has(index) && (
                              <p className="text-xs text-amber-700">{columnWarnings.get(index)}</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="space-y-4">
            <FormField
              label="Origin"
              helpText="Shown against every lead in this import so you can find them later."
            >
              <Input
                value={leadSource}
                onChange={(e) => setLeadSource(e.target.value)}
                maxLength={100}
                placeholder="e.g. Spring event sign-ups"
              />
            </FormField>

            <div className="space-y-4 border-t border-border pt-4">
              <p className="text-sm text-slate-700">
                Use these for every lead that doesn&apos;t have its own. Anything your file
                provides is used instead.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField label="Subject">
                  <Input
                    value={defaultSubject}
                    onChange={(e) => setDefaultSubject(e.target.value)}
                    maxLength={200}
                    placeholder="e.g. Spring event enquiry"
                  />
                </FormField>
                {defaultable.map(({ field, label, entries }) => (
                  <FormField key={field} label={label}>
                    <Select<string>
                      options={[
                        { label: 'Leave empty', value: '' },
                        ...entries.map((entry) => ({ label: entry.label, value: entry.id })),
                      ]}
                      value={defaults[field] ?? ''}
                      onChange={(value) => setDefaultFor(field, value)}
                    />
                  </FormField>
                ))}
              </div>
            </div>

            <Checkbox
              label="Skip leads that are already in Prospera"
              description="Matched on email address, or on name and phone number when there's no email. A lead with neither is always added."
              checked={skipDuplicates}
              onChange={(e) => setSkipDuplicates(e.target.checked)}
            />

            <div className="flex justify-end border-t border-border pt-4">
              <Button onClick={runImport} disabled={!ready}>
                Add {rowCount.toLocaleString()} {rowCount === 1 ? 'lead' : 'leads'}
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </Card>
        </>
      )}

      {step === 'importing' && (
        <Card className="space-y-3">
          <p className="font-medium text-slate-900">
            Adding leads… {progress.toLocaleString()} of {rowCount.toLocaleString()}
          </p>
          <div className="h-2 w-full overflow-hidden rounded bg-slate-200">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${rowCount === 0 ? 0 : Math.round((progress / rowCount) * 100)}%` }}
            />
          </div>
          <p className="text-sm text-slate-600">Please keep this page open until it finishes.</p>
        </Card>
      )}

      {step === 'summary' && (
        <>
          <Card className="space-y-3">
            {importError && (
              <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {importError}
              </p>
            )}

            <p className="text-lg font-medium text-slate-900">{summaryLine(tally)}</p>

            {unattempted > 0 && (
              <p className="text-sm text-slate-600">
                {unattempted.toLocaleString()}{' '}
                {unattempted === 1 ? 'lead was' : 'leads were'} not reached. They are in the
                download below, so you can add just those without repeating the ones that
                worked.
              </p>
            )}

            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={() => navigate('/leads')}>View leads</Button>
              <Button variant="secondary" onClick={startOver}>
                Add another file
              </Button>
              {hasDownload && (
                <Button variant="ghost" onClick={downloadUnimported}>
                  <Download className="mr-1 h-4 w-4" />
                  Download the rows that weren't added
                </Button>
              )}
            </div>
          </Card>

          {tally.results.length > 0 && (
            <Card noPadding>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-border bg-slate-50 text-left text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-4 py-3">Row</th>
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">What happened</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tally.results.map((result) => (
                      <tr key={result.row} className="border-b border-border last:border-0">
                        <td className="px-4 py-3 text-slate-500">{sourceLine(result.row)}</td>
                        <td className="px-4 py-3 text-slate-900">{result.name || '—'}</td>
                        <td className="px-4 py-3 text-slate-600">
                          {result.reason ?? result.warnings.join(' ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

/**
 * What to show when the server could not read a workbook.
 *
 * The reader's own messages are already written for the user ("that sheet has
 * 40,000 rows…"), so they are worth surfacing; anything else is not, for the
 * same reason importErrorMessage exists.
 */
export function spreadsheetErrorMessage(error: unknown): string {
  const fallback =
    "We couldn't read that spreadsheet. Save it as CSV and try again, and if it "
    + 'keeps happening contact Prospera support.';

  return (error as { isAxiosError?: boolean } | null)?.isAxiosError === true
    ? getErrorMessage(error, fallback)
    : fallback;
}

const IMPORT_FALLBACK =
  "We couldn't add these leads. Try again, and if it keeps happening contact Prospera support.";

/**
 * The message to show when an import stops.
 *
 * A reply from the server is worth showing — "You do not have permission to
 * import leads" tells the user exactly what to do. Anything else is not:
 * `getErrorMessage` returns `error.message` verbatim for a non-Axios Error, so
 * a JavaScript failure would put something like "Cannot read properties of
 * undefined" in front of a front-desk user. Those get plain words instead.
 */
export function importErrorMessage(error: unknown): string {
  const fromServer = (error as { isAxiosError?: boolean } | null)?.isAxiosError === true;

  return fromServer ? getErrorMessage(error, IMPORT_FALLBACK) : IMPORT_FALLBACK;
}

// The "not added" file returns the user's own text — and the file may have
// come from someone else — so its cells are defused before download.
export { defuseFormula };

function readFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    // UTF-8 covers Excel's "CSV UTF-8" and anything exported by a modern CRM.
    // A file saved as plain Windows CSV may show accented characters oddly;
    // the mapping step's examples make that visible before anything is added.
    reader.readAsText(file, 'utf-8');
  });
}

/** Plain-language count. Only mentions what actually happened. */
export function summaryLine(tally: LeadImportTally): string {
  const parts: string[] = [
    `Added ${tally.imported.toLocaleString()} ${tally.imported === 1 ? 'lead' : 'leads'}.`,
  ];

  if (tally.skipped > 0) {
    parts.push(
      tally.skipped === 1
        ? '1 was already in your leads.'
        : `${tally.skipped.toLocaleString()} were already in your leads.`,
    );
  }

  if (tally.failed > 0) {
    parts.push(
      tally.failed === 1
        ? "1 couldn't be read."
        : `${tally.failed.toLocaleString()} couldn't be read.`,
    );
  }

  return parts.join(' ');
}

function defaultLeadSource(fileName: string): string {
  return stripExtension(fileName).slice(0, 100);
}

function stripExtension(name: string): string {
  return name.replace(/\.[^.]+$/, '');
}

function download(name: string, contents: string) {
  // Leading BOM so Excel opens the file as UTF-8 rather than the system
  // codepage, which is what turns an accented name into mojibake.
  const byteOrderMark = String.fromCharCode(0xfeff);
  const blob = new Blob([byteOrderMark + contents], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

function downloadSample() {
  download(
    'example leads.csv',
    toCsv([
      ['First Name', 'Last Name', 'Email', 'Phone', 'City', 'State', 'Lead Source', 'Notes'],
      ['Jane', 'Doe', 'jane@example.com', '555-010-1234', 'Nashville', 'TN', 'Spring event', 'Asked about IV therapy'],
      ['John', 'Roe', 'john@example.com', '555-010-5678', 'Franklin', 'TN', 'Website', ''],
    ]),
  );
}

export default LeadsImportPage;
